import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import AdminLayout from '../../components/admin/AdminLayout';
import { useToast } from '../../contexts/ToastContext';
import { getCms, updateCms } from '../../api/client';
import {
  Save,
  Globe,
  FileText,
  Share2,
  Bell,
  Shield,
  Plus,
  Trash2,
  Loader2,
} from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';

interface SocialLink {
  platform: string;
  url: string;
}

export default function AdminSettings(): React.JSX.Element {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [settings, setSettings] = useState<Record<string, any>>({});
  const [socialLinks, setSocialLinks] = useState<SocialLink[]>([]);

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const [settingsRes, socialRes] = await Promise.all([
          getCms('settings'),
          getCms('social-links'),
        ]);
        setSettings(settingsRes.data || {});
        setSocialLinks(Array.isArray(socialRes.data) ? socialRes.data : []);
      } catch (err: any) {
        toast.show(err?.message || 'Failed to load settings', 'error');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [toast]);

  const updateSetting = (key: string, value: any) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (activeTab === 'general' || activeTab === 'seo') {
        await updateCms('settings', settings);
      } else if (activeTab === 'social') {
        await updateCms('social-links', socialLinks);
      }
      toast.show('Settings saved successfully', 'success');
    } catch (err: any) {
      toast.show(err?.message || 'Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const addSocialLink = () => {
    setSocialLinks((prev) => [...prev, { platform: '', url: '' }]);
  };

  const updateSocialLink = (
    index: number,
    field: keyof SocialLink,
    value: string
  ) => {
    setSocialLinks((prev) =>
      prev.map((link, i) => (i === index ? { ...link, [field]: value } : link))
    );
  };

  const deleteSocialLink = (index: number) => {
    setSocialLinks((prev) => prev.filter((_, i) => i !== index));
  };

  const tabs = [
    { id: 'general', label: 'General', icon: Globe },
    { id: 'seo', label: 'SEO', icon: FileText },
    { id: 'social', label: 'Social Links', icon: Share2 },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Security', icon: Shield },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
          <p className="text-sm text-slate-500">
            Manage your website configuration
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar Tabs */}
          <div className="lg:w-64 flex lg:flex-col gap-2 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'bg-amber-50 text-amber-600'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Content */}
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex-1 bg-white rounded-2xl border border-slate-100 p-6"
          >
            {loading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
              </div>
            )}

            {!loading && activeTab === 'general' && (
              <div className="space-y-5">
                <h2 className="text-lg font-bold text-slate-900 mb-4">
                  General Settings
                </h2>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Input
                    label="Site Name"
                    value={settings.site_name || ''}
                    onChange={(e) => updateSetting('site_name', e.target.value)}
                  />
                  <Input
                    label="Contact Email"
                    type="email"
                    value={settings.contact_email || ''}
                    onChange={(e) =>
                      updateSetting('contact_email', e.target.value)
                    }
                  />
                  <Input
                    label="Contact Phone"
                    value={settings.contact_phone || ''}
                    onChange={(e) =>
                      updateSetting('contact_phone', e.target.value)
                    }
                  />
                  <Input
                    label="Address"
                    value={settings.contact_address || ''}
                    onChange={(e) =>
                      updateSetting('contact_address', e.target.value)
                    }
                  />
                  <Input
                    label="Footer Text"
                    value={settings.footer_text || ''}
                    onChange={(e) =>
                      updateSetting('footer_text', e.target.value)
                    }
                  />
                </div>
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                  <p className="text-sm font-semibold text-slate-900 mb-2">
                    Logo
                  </p>
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-xl bg-amber-500 flex items-center justify-center text-white text-2xl font-bold">
                      {(settings.site_name || 'E').charAt(0)}
                    </div>
                    <div>
                      <button className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
                        Change Logo
                      </button>
                      <p className="text-xs text-slate-500 mt-1">
                        Recommended: 512x512px PNG
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!loading && activeTab === 'seo' && (
              <div className="space-y-5">
                <h2 className="text-lg font-bold text-slate-900 mb-4">
                  SEO Settings
                </h2>
                <Input
                  label="Default SEO Title"
                  value={settings.meta_title || ''}
                  onChange={(e) => updateSetting('meta_title', e.target.value)}
                />
                <Input
                  label="Default Meta Description"
                  multiline
                  value={settings.meta_description || ''}
                  onChange={(e) =>
                    updateSetting('meta_description', e.target.value)
                  }
                />
                <Input
                  label="Meta Keywords"
                  value={settings.meta_keywords || ''}
                  onChange={(e) =>
                    updateSetting('meta_keywords', e.target.value)
                  }
                />
                <Input
                  label="Google Analytics ID"
                  value={settings.google_analytics_id || ''}
                  onChange={(e) =>
                    updateSetting('google_analytics_id', e.target.value)
                  }
                />
                <Input
                  label="Google Tag Manager ID"
                  value={settings.google_tag_manager_id || ''}
                  onChange={(e) =>
                    updateSetting('google_tag_manager_id', e.target.value)
                  }
                />
                <Input
                  label="Meta (Facebook/Instagram) Pixel ID"
                  value={settings.meta_pixel_id || ''}
                  onChange={(e) =>
                    updateSetting('meta_pixel_id', e.target.value)
                  }
                />
                <Input
                  label="Google Ads Conversion ID (AW-XXXXXXXXX)"
                  value={settings.google_ads_conversion_id || ''}
                  onChange={(e) =>
                    updateSetting('google_ads_conversion_id', e.target.value)
                  }
                />
                <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
                  <input
                    type="checkbox"
                    id="sitemap"
                    checked={!!settings.auto_sitemap}
                    onChange={(e) =>
                      updateSetting('auto_sitemap', e.target.checked)
                    }
                    className="w-4 h-4 rounded border-slate-300"
                  />
                  <label htmlFor="sitemap" className="text-sm text-slate-700">
                    Auto-generate sitemap.xml
                  </label>
                </div>
              </div>
            )}

            {!loading && activeTab === 'social' && (
              <div className="space-y-5">
                <h2 className="text-lg font-bold text-slate-900 mb-4">
                  Social Media Links
                </h2>
                <div className="space-y-3">
                  {socialLinks.map((link, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100"
                    >
                      <div className="flex-1 grid sm:grid-cols-2 gap-3">
                        <Input
                          label="Platform"
                          placeholder="e.g. LinkedIn"
                          value={link.platform}
                          onChange={(e) =>
                            updateSocialLink(index, 'platform', e.target.value)
                          }
                        />
                        <Input
                          label="URL"
                          placeholder="https://..."
                          value={link.url}
                          onChange={(e) =>
                            updateSocialLink(index, 'url', e.target.value)
                          }
                        />
                      </div>
                      <button
                        onClick={() => deleteSocialLink(index)}
                        className="mt-6 p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete link"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <Button
                  variant="outline"
                  onClick={addSocialLink}
                  leftIcon={<Plus className="w-4 h-4" />}
                >
                  Add Link
                </Button>
              </div>
            )}

            {!loading && activeTab === 'notifications' && (
              <div className="space-y-5">
                <h2 className="text-lg font-bold text-slate-900 mb-4">
                  Notification Settings
                </h2>
                {[
                  { label: 'New enrollment email to admin', checked: true },
                  { label: 'Payment success email to student', checked: true },
                  { label: 'Payment failure email to admin', checked: true },
                  { label: 'New lead email to admin', checked: true },
                  { label: 'Workshop reminder (24h before)', checked: true },
                  { label: 'Course completion certificate', checked: true },
                  { label: 'WhatsApp enrollment confirmation', checked: true },
                  { label: 'WhatsApp payment reminder', checked: false },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-xl"
                  >
                    <span className="text-sm text-slate-700">{item.label}</span>
                    <input
                      type="checkbox"
                      defaultChecked={item.checked}
                      className="w-5 h-5 rounded border-slate-300 text-amber-500"
                    />
                  </div>
                ))}
              </div>
            )}

            {!loading && activeTab === 'security' && (
              <div className="space-y-5">
                <h2 className="text-lg font-bold text-slate-900 mb-4">
                  Security Settings
                </h2>
                <Input
                  label="Admin Emails (comma separated)"
                  defaultValue="rajesh@ecoleminds.com, admin@ecoleminds.com"
                />
                <Input
                  label="reCAPTCHA Site Key"
                  type="password"
                  defaultValue="6Lc..."
                />
                <Input
                  label="reCAPTCHA Secret Key"
                  type="password"
                  defaultValue="6Lc..."
                />
                <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
                  <input
                    type="checkbox"
                    id="2fa"
                    className="w-4 h-4 rounded border-slate-300"
                  />
                  <label htmlFor="2fa" className="text-sm text-slate-700">
                    Enable 2FA for admin accounts
                  </label>
                </div>
                <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
                  <input
                    type="checkbox"
                    id="audit"
                    defaultChecked
                    className="w-4 h-4 rounded border-slate-300"
                  />
                  <label htmlFor="audit" className="text-sm text-slate-700">
                    Enable audit logging
                  </label>
                </div>
              </div>
            )}

            {!loading && (
              <div className="pt-6 border-t border-slate-100 mt-6">
                <Button
                  onClick={handleSave}
                  isLoading={saving}
                  leftIcon={<Save className="w-4 h-4" />}
                >
                  Save Changes
                </Button>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </AdminLayout>
  );
}
